import {h} from 'panel';

export interface FormatTextOptions {
  /** optional classname for the returned root div */
  className?: string;
  /** add ellipsis descriptions longer than maxLength. Default is no truncation */
  maxLength?: number;
}

interface FormatElementBase<T> {
  type: T;
  text: string;
}

type FormatPartSpan = FormatElementBase<'span'>;
interface FormatPartAnchor extends FormatElementBase<'a'> {
  href: string;
  target?: string;
}

type FormatPart = FormatPartSpan | FormatPartAnchor;

/**
 * Returns a snabbdom vnode with markdown like text formatted into spans and links
 */
// TODO
// eslint-disable-next-line sonarjs/cognitive-complexity
export function formatText(text: string, {className = ``, maxLength = 1000}: FormatTextOptions = {}) {
  // This regex matches naked links and markdown-formatted links
  // e.g [google](https://google.com) and https://google.com and mailto:email@mixpanel.com
  const urlRegex = /\[([^\]]+)\]\(((https?:\/\/\S+)|(mailto:\S+)|(\/\S*))\)(?:{:target="([a-z_]+)"})?|(https?:\/\/\S+)/gm;
  // parse text into span / anchor parts
  const parseParts: FormatPart[] = [];
  let lastIndex = 0;
  let urlMatch: RegExpExecArray;
  do {
    urlMatch = urlRegex.exec(text);
    if (urlMatch) {
      // add text upto link
      parseParts.push({type: `span`, text: text.slice(lastIndex, urlMatch.index)});
      const [fullMatch, mdLinkText, mdLinkHref] = urlMatch;
      const nakedLinkHref = urlMatch[7];
      if (nakedLinkHref) {
        parseParts.push({type: `a`, href: nakedLinkHref, text: nakedLinkHref, target: `_blank`});
      } else if (mdLinkText) {
        parseParts.push({type: `a`, href: mdLinkHref, text: mdLinkText, target: urlMatch[6] || `_blank`});
      }
      lastIndex = urlMatch.index + fullMatch.length;
    }
  } while (urlMatch !== null);
  // add remaining text
  parseParts.push({type: `span`, text: text.slice(lastIndex)});

  // generate the vdom children and truncate text if necessary
  const hParts = [];
  let lastLength = 0;
  for (const part of parseParts) {
    if (lastLength < maxLength && part.text) {
      const partLength = part.text.length;
      // prettier-ignore
      const partText = (partLength + lastLength) <= maxLength
        ? part.text
        : part.text.substr(0, maxLength - lastLength) + `...`;
      lastLength += partLength;

      if (part.type === `a`) {
        hParts.push(h(part.type, {attrs: {href: part.href, target: part.target}}, partText));
      } else {
        hParts.push(h(part.type, {}, partText));
      }
    }
  }

  return h(`div`, {class: {[className]: !!className}}, hParts);
}
